import {
  parseNonNegativeNumber,
  type NumberRange,
} from "@shared/validation/number-in-range";

export const LENGTH_UNITS = ["in", "ft"] as const;
export type LengthUnit = (typeof LENGTH_UNITS)[number];

const MAX_FILL_PERCENT = 100;

export const FILL_PERCENT_RANGE: NumberRange = { min: 0, max: MAX_FILL_PERCENT };

const INCHES_PER_FOOT = 12;
const CUBIC_INCHES_PER_CUBIC_FOOT = 1728;
const CUBIC_FEET_PER_CUBIC_YARD = 27;

export interface GaylordVolumeInput {
  length: number;
  width: number;
  height: number;
  unit: LengthUnit;
  fillPercent: number;
  densityLbsPerCubicFoot: number;
}

export interface GaylordVolumeEstimate {
  totalCubicFeet: number;
  usableCubicFeet: number;
  usableCubicYards: number;
  payloadLbs: number;
}

function toInches(value: number, unit: LengthUnit): number {
  return unit === "in" ? value : value * INCHES_PER_FOOT;
}

function fromInches(inches: number, unit: LengthUnit): number {
  return unit === "in" ? inches : inches / INCHES_PER_FOOT;
}

const DISPLAY_DECIMALS = 2;

export interface LengthEntry {
  text: string;
  entered: { text: string; unit: LengthUnit } | null;
}

export function typedLength(text: string): LengthEntry {
  return { text, entered: null };
}

export function lengthValue(entry: LengthEntry, unit: LengthUnit): number | null {
  if (entry.entered === null) return parseNonNegativeNumber(entry.text);
  const entered = parseNonNegativeNumber(entry.entered.text);
  return entered === null ? null : fromInches(toInches(entered, entry.entered.unit), unit);
}

export function convertLength(entry: LengthEntry, from: LengthUnit, to: LengthUnit): LengthEntry {
  if (from === to) return entry;
  const value = lengthValue(entry, from);
  if (value === null) return entry;
  const entered = entry.entered ?? { text: entry.text, unit: from };
  if (entered.unit === to) return { text: entered.text, entered };
  const shown = fromInches(toInches(value, from), to);
  return { text: String(Number(shown.toFixed(DISPLAY_DECIMALS))), entered };
}

export function estimateGaylordVolume(input: GaylordVolumeInput): GaylordVolumeEstimate {
  const { unit, ...measures } = input;
  for (const [name, value] of Object.entries(measures)) {
    if (!Number.isFinite(value) || value < 0) {
      throw new RangeError(`${name} must be a finite number of zero or more`);
    }
  }
  if (input.fillPercent > MAX_FILL_PERCENT) {
    throw new RangeError(`fillPercent must be ${MAX_FILL_PERCENT} or less`);
  }
  const cubicInches = toInches(input.length, unit) * toInches(input.width, unit) * toInches(input.height, unit);
  const totalCubicFeet = cubicInches / CUBIC_INCHES_PER_CUBIC_FOOT;
  const usableCubicFeet = totalCubicFeet * (input.fillPercent / 100);
  return {
    totalCubicFeet,
    usableCubicFeet,
    usableCubicYards: usableCubicFeet / CUBIC_FEET_PER_CUBIC_YARD,
    payloadLbs: usableCubicFeet * input.densityLbsPerCubicFoot,
  };
}
